import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { db } from '../../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, orderBy } from 'firebase/firestore';
import {
  Globe,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  ExternalLink,
  Trash2,
  Copy,
  QrCode
} from 'lucide-react';
import toast from 'react-hot-toast';

const Websites = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canCreateWebsite, planLimits } = useSubscription();
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [websiteToDelete, setWebsiteToDelete] = useState(null);
  const [showActionsFor, setShowActionsFor] = useState(null);

  useEffect(() => {
    if (user) {
      fetchWebsites();
    }
  }, [user]);

  const fetchWebsites = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'websites'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const websitesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setWebsites(websitesList);
    } catch (error) {
      console.error('Error fetching websites:', error);
      toast.error('Failed to load websites');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebsite = async () => {
    const canCreate = await canCreateWebsite();
    if (!canCreate) {
      toast.error('You have reached your website limit. Please upgrade your plan.');
      navigate('/plans');
      return;
    }
    navigate('/site/wizard');
  };

  const handleDelete = async () => {
    if (!websiteToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'websites', websiteToDelete.id));
      toast.success('Website deleted successfully');
      setWebsites(websites.filter(w => w.id !== websiteToDelete.id));
      setShowDeleteModal(false);
      setWebsiteToDelete(null);
    } catch (error) {
      console.error('Error deleting website:', error);
      toast.error('Failed to delete website');
    }
  };

  const handleDuplicate = async (website) => {
    const canCreate = await canCreateWebsite();
    if (!canCreate) {
      toast.error('You have reached your website limit. Please upgrade your plan.');
      return;
    }
    
    toast.success('Website duplicated successfully');
    // In production, implement actual duplication logic
  };

  const filteredWebsites = websites.filter(website => {
    const matchesSearch = website.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         website.domain?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || website.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Websites</h1>
          <p className="text-gray-500">
            {websites.length} of {planLimits?.websites || 1} websites used
          </p>
        </div>
        <button
          onClick={handleCreateWebsite}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Website
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search websites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      {/* Websites Grid */}
      {filteredWebsites.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWebsites.map((website) => (
            <div
              key={website.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Preview */}
              <div className="h-40 bg-gradient-to-br from-primary-100 to-purple-100 flex items-center justify-center relative">
                {website.thumbnail ? (
                  <img
                    src={website.thumbnail}
                    alt={website.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Globe className="w-16 h-16 text-primary-300" />
                )}
                <span className={`absolute top-3 right-3 px-2 py-1 text-xs font-medium rounded-full ${
                  website.status === 'published'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {website.status || 'draft'}
                </span>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{website.name}</h3>
                    <p className="text-sm text-gray-500">{website.domain || 'No domain'}</p>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setShowActionsFor(showActionsFor === website.id ? null : website.id)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    
                    {showActionsFor === website.id && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                        <button
                          onClick={() => navigate(`/site/editor/${website.id}`)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => navigate(`/site/user-kit?website=${website.id}`)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <QrCode className="w-4 h-4" />
                          QR Code
                        </button>
                        <button
                          onClick={() => handleDuplicate(website)}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        >
                          <Copy className="w-4 h-4" />
                          Duplicate
                        </button>
                        {website.status === 'published' && website.domain && (
                          <a
                            href={`https://${website.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Visit
                          </a>
                        )}
                        <hr className="my-1" />
                        <button
                          onClick={() => {
                            setWebsiteToDelete(website);
                            setShowDeleteModal(true);
                            setShowActionsFor(null);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                  <span>Updated {new Date(website.updatedAt?.toDate?.() || website.updatedAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
          <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery ? 'No websites found' : 'No websites yet'}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery 
              ? 'Try adjusting your search or filter'
              : 'Create your first website to get started'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleCreateWebsite}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
            >
              <Plus className="w-5 h-5" />
              Create Website
            </button>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Website
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{websiteToDelete?.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setWebsiteToDelete(null);
                }}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Websites;
